import React from "react";
import { Box, Avatar, Typography, Stack, Tooltip } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { useSelector } from 'react-redux';

const steps = [
  { label: "Basic Details", icon: AssignmentIndOutlinedIcon, color: '#7522D1' },
  { label: "Personal Information", icon: PersonOutlineIcon, color: '#3B82F6' },
  { label: "Bank Details", icon: AccountBalanceOutlinedIcon, color: '#10B981' },
  { label: "Education", icon: SchoolOutlinedIcon, color: '#F59E0B' },
  { label: "Previous Employment", icon: WorkOutlineIcon, color: '#EF4444' },
  { label: "CTC Information", icon: PaymentsOutlinedIcon, color: '#8B5CF6' },
  { label: "Joining Forms", icon: DescriptionOutlinedIcon, color: '#06B6D4' },
];

const ProfileDetails = ({ activeStep = 0, setActiveStep, employeeId }) => {
  const stepCompletion = useSelector((state) => state.basicDetails.stepCompletion) || {};
  const formData = useSelector((state) => state.basicDetails.formData) || {};

  const isStepCompleted = (stepIndex) => {
    const stepKeys = ['basicDetails', 'personalInformation', 'bankDetails', 'education', 'previousEmployment', 'compensationBenefits', 'joiningForms'];
    return stepCompletion[stepKeys[stepIndex]] || false;
  };

  const isStepDisabled = (stepIndex) => {
    if (stepIndex === 0) return false;
    if (!formData.employeeId && !employeeId) return true;
    return false;
  };

  // Progress calculation
  const completedCount = steps.filter((_, i) => isStepCompleted(i)).length;

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 280 },
        height: { xs: 'auto', md: 'calc(100vh - 64px)' },
        borderRight: { md: '1px solid rgba(117,34,209,0.08)' },
        borderBottom: { xs: '1px solid rgba(117,34,209,0.08)', md: 'none' },
        background: 'linear-gradient(180deg, #FDFBFF 0%, #F9F5FE 100%)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header — Avatar & Progress (desktop only) */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center', px: 3, pt: 4, pb: 3 }}>
        {/* Avatar */}
        <Box sx={{ position: 'relative', mb: 2 }}>
          <Box sx={{
            position: 'absolute', inset: -4,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #7522D1 0%, #C084FC 100%)',
            opacity: 0.25,
          }} />
          <Avatar
            src={formData.passportPhotoPath || undefined}
            sx={{
              width: 88,
              height: 88,
              bgcolor: '#F5EEFC',
              color: '#7522D1',
              fontSize: '2rem',
              fontWeight: 900,
              border: '3px solid #FFFFFF',
              boxShadow: '0 8px 24px rgba(117,34,209,0.15)',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {formData.firstName ? (
              `${formData.firstName[0] || ''}${formData.lastName?.[0] || ''}`.toUpperCase()
            ) : (
              <PersonOutlineIcon sx={{ fontSize: 36 }} />
            )}
          </Avatar>
        </Box>

        {/* Name */}
        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F0A1E', letterSpacing: '-0.01em', textAlign: 'center', lineHeight: 1.2 }}>
          {formData.firstName || formData.lastName
            ? `${formData.firstName || ''} ${formData.lastName || ''}`.trim()
            : 'New Employee'}
        </Typography>
        {formData.emailAddress && (
          <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: 600, mt: 0.5, textAlign: 'center' }} noWrap>
            {formData.emailAddress}
          </Typography>
        )}

      </Box>

      {/* Divider */}
      <Box sx={{ display: { xs: 'none', md: 'block' }, mx: 3, borderTop: '1px solid rgba(117,34,209,0.08)', mb: 2 }} />

      {/* Section Label */}
      <Box sx={{ px: 3, mb: 1.5, display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.65rem' }}>
          Onboarding Steps
        </Typography>
      </Box>

      {/* Steps List */}
      <Stack
        direction={{ xs: 'row', md: 'column' }}
        spacing={0.75}
        sx={{
          px: { xs: 1.5, md: 2 },
          py: { xs: 1.5, md: 0 },
          maxHeight: { xs: 'auto', md: 'calc(100vh - 380px)' },
          overflowX: { xs: 'auto', md: 'hidden' },
          overflowY: { xs: 'hidden', md: 'auto' },
          pb: { xs: 1, md: 2 },
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {steps.map((s, idx) => {
          const isActive = idx === activeStep;
          const isCompleted = isStepCompleted(idx);
          const isDisabled = isStepDisabled(idx);
          const IconComponent = s.icon;

          return (
            <Tooltip
              key={s.label}
              title={isDisabled ? 'Complete Basic Details first' : s.label}
              placement="right"
              disableHoverListener={!isDisabled}
            >
              <Box
                onClick={() => !isDisabled && setActiveStep(idx)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  borderRadius: '12px',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  minWidth: { xs: 'max-content', md: 'auto' },
                  flexShrink: 0,
                  position: 'relative',
                  border: '1.5px solid',
                  borderColor: isActive
                    ? s.color
                    : isCompleted
                      ? 'rgba(16,185,129,0.2)'
                      : 'transparent',
                  bgcolor: isActive
                    ? `${s.color}12`
                    : isCompleted
                      ? 'rgba(16,185,129,0.05)'
                      : 'transparent',
                  opacity: isDisabled ? 0.45 : 1,
                  transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
                  '&:hover': !isDisabled ? {
                    bgcolor: isActive ? `${s.color}12` : '#F5EEFC',
                    borderColor: isActive ? s.color : 'rgba(117,34,209,0.2)',
                    transform: 'translateX(3px)',
                  } : {},
                }}
              >
                {/* Step number / Icon */}
                <Box sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: isActive
                    ? s.color
                    : isCompleted
                      ? 'rgba(16,185,129,0.12)'
                      : '#F3F4F6',
                  flexShrink: 0,
                  transition: 'all 0.2s',
                }}>
                  {isCompleted && !isActive ? (
                    <CheckCircleRoundedIcon sx={{ fontSize: 18, color: '#10B981' }} />
                  ) : (
                    <IconComponent sx={{ fontSize: 17, color: isActive ? '#FFFFFF' : '#6B7280' }} />
                  )}
                </Box>

                {/* Label */}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isActive ? 800 : isCompleted ? 700 : 600,
                    color: isActive ? s.color : isCompleted ? '#065F46' : '#374151',
                    fontSize: '0.85rem',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.label}
                </Typography>

                {/* Active right arrow */}
                {isActive && (
                  <Box sx={{
                    ml: 'auto',
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: s.color,
                    flexShrink: 0,
                  }} />
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ProfileDetails;
